import React, { useState } from 'react';
import { useProjects } from '../../lib/ProjectContext';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import ProjectList from './ProjectList';

interface DashboardProps {
  onCreateNew: () => void;
  onOpenProject: (projectId: string) => Promise<void>;
  onLoginRequired: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onCreateNew, onOpenProject, onLoginRequired }) => {
  const { currentUser } = useAuth();
  const { projects } = useProjects();
  const [searchQuery, setSearchQuery] = useState('');
  
  if (!currentUser) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Dashboard</CardTitle>
          <CardDescription>You need to be logged in to view your projects</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Please login to access your saved projects and create new ones.
          </p>
          <Button onClick={onLoginRequired}>Login / Sign Up</Button>
        </CardContent>
      </Card>
    );
  }

  // Count active projects (non-archived)
  const activeProjectCount = projects.filter(project => !project.archived).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Dashboard</CardTitle>
            <CardDescription>
              {activeProjectCount > 0 
                ? `${activeProjectCount} active estimate${activeProjectCount !== 1 ? 's' : ''} sorted by most recently updated`
                : 'No active estimates'}
            </CardDescription>
          </div>
          <div className="relative">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="search"
              placeholder="Search projects..."
              className="pl-8 pr-8 py-2 h-9 w-64 rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <ProjectList onOpenProject={onOpenProject} searchQuery={searchQuery} />
            
            <div className="flex justify-center pt-4">
              <Button onClick={onCreateNew}>Create New Estimate</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard; 